import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

/**
 * Full-width form select for modal/form contexts.
 *
 * Renders a <button> trigger (not a native <select>) at exactly 44px (h-11),
 * matching sibling <input> fields that use .input-admin / .input (h-11).
 * Uses flex + items-center for vertical centering instead of vertical padding.
 *
 * The dropdown list is rendered via a React Portal with fixed positioning so it
 * is never clipped by parent overflow containers (e.g. modal-content scrollable areas).
 *
 * Theme support:
 *   admin    → red focus ring, red selection highlight  (matches .select-admin)
 *   employee → blue focus ring, blue selection highlight (matches .select)
 *
 * @example
 * <FormSelect
 *   options={[{ value: 'a', label: 'Alpha' }]}
 *   value={selected}
 *   onChange={setSelected}
 *   placeholder="Select reason..."
 *   theme="admin"
 *   ariaLabel="Reason"
 *   required
 * />
 */
const FormSelect = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  theme = 'admin',
  disabled = false,
  required = false,
  className = '',
  ariaLabel,
}) => {
  const isAdmin = theme === 'admin';
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [typeAhead, setTypeAhead] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState({});
  const typeAheadTimer = useRef(null);
  const listRef = useRef(null);
  const buttonRef = useRef(null);
  const containerRef = useRef(null);
  const listboxId = useId();

  // Click-outside that accounts for the portaled dropdown list.
  // useClickOutside only checks one ref, so it misses the portal and
  // closes the dropdown before the option's onClick can fire.
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectableOptions = options.filter(o => !o.isGroupHeader);

  const selected = selectableOptions.find(o => String(o.value) === String(value));
  const label = selected ? selected.label : placeholder;

  // Calculate dropdown position whenever it opens
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownMaxHeight = 288; // 72 * 4 = max-h-72
    const spaceBelow = viewportHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;

    // Prefer opening downward; flip upward only if there's more space above
    const openUpward = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      // Above sticky modal header/footer (10001) — same as DatePickerField
      zIndex: 10002,
      ...(openUpward
        ? { bottom: viewportHeight - rect.top + 4, maxHeight: Math.min(spaceAbove, dropdownMaxHeight) }
        : { top: rect.bottom + 4, maxHeight: Math.min(spaceBelow, dropdownMaxHeight) }),
    });
  }, [isOpen]);

  // Recompute position on scroll/resize while open
  useEffect(() => {
    if (!isOpen) return;
    const update = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownMaxHeight = 288;
      const spaceBelow = viewportHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const openUpward = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;
      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        zIndex: 10002,
        ...(openUpward
          ? { bottom: viewportHeight - rect.top + 4, maxHeight: Math.min(spaceAbove, dropdownMaxHeight) }
          : { top: rect.bottom + 4, maxHeight: Math.min(spaceBelow, dropdownMaxHeight) }),
      });
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setHighlightIdx(-1);
  }, [isOpen]);

  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const el = listRef.current.children[highlightIdx];
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  const select = useCallback((optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  }, [onChange]);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIdx(prev => (prev + 1) % selectableOptions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIdx(prev => (prev - 1 + selectableOptions.length) % selectableOptions.length);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (highlightIdx >= 0 && selectableOptions[highlightIdx]) {
          select(selectableOptions[highlightIdx].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
      case 'Home':
        e.preventDefault();
        setHighlightIdx(0);
        break;
      case 'End':
        e.preventDefault();
        setHighlightIdx(selectableOptions.length - 1);
        break;
      default:
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
          const next = typeAhead + e.key.toLowerCase();
          setTypeAhead(next);
          clearTimeout(typeAheadTimer.current);
          typeAheadTimer.current = setTimeout(() => setTypeAhead(''), 500);

          const matchIdx = selectableOptions.findIndex(o =>
            o.label.toLowerCase().startsWith(next)
          );
          if (matchIdx >= 0) setHighlightIdx(matchIdx);
        }
        break;
    }
  }, [isOpen, highlightIdx, selectableOptions, select, typeAhead]);

  const focusRing = isAdmin
    ? 'focus:ring-red-500 focus:border-red-500'
    : 'focus:ring-blue-500 focus:border-blue-500';

  const selectedHighlight = isAdmin
    ? 'text-red-700 font-medium bg-red-50'
    : 'text-blue-700 font-medium bg-blue-50';

  const dropdownList = isOpen && (
    <div
      id={listboxId}
      role="listbox"
      ref={listRef}
      style={dropdownStyle}
      className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-y-auto"
    >
      {(() => {
        let selectableIdx = 0;
        return options.map((option, rawIdx) => {
          if (option.isGroupHeader) {
            return (
              <div
                key={`group-${rawIdx}`}
                className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-t border-gray-100 first:border-t-0"
              >
                {option.label}
              </div>
            );
          }
          const idx = selectableIdx++;
          return (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={String(value) === String(option.value)}
              onMouseDown={(e) => {
                e.preventDefault();
                select(option.value);
              }}
              onMouseEnter={() => setHighlightIdx(idx)}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                String(value) === String(option.value)
                  ? selectedHighlight
                  : highlightIdx === idx
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {option.label}
            </button>
          );
        });
      })()}
      {selectableOptions.length === 0 && (
        <div className="px-4 py-4 text-sm text-gray-400 text-center">No options</div>
      )}
    </div>
  );

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        role="combobox"
        aria-label={ariaLabel}
        aria-controls={listboxId}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-required={required}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`flex items-center justify-between w-full h-11 px-4 border border-gray-300 rounded-full shadow-sm bg-white text-left focus:outline-none focus:ring-2 ${focusRing} transition-[border-color,box-shadow] duration-200 ${
          disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'cursor-pointer'
        } ${className}`}
      >
        <span className={`truncate ${selected ? 'text-gray-900' : 'text-gray-400'}`}>
          {label}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {typeof document !== 'undefined' && createPortal(dropdownList, document.body)}
    </div>
  );
};

export default FormSelect;
